/**
 * Installing the app (doc 05 › PWA): a gentle tip after someone's **second** visit,
 * dismissible, and shown once.
 *
 * All of this is per-device on purpose, so it lives in `localStorage` rather than in the
 * server-side dismissed hints: "installed" is true of a phone, not of an account, and
 * being nudged on your laptop about a phone home screen would be nonsense.
 */
const VISITS_KEY = 'gg.visits';
const DISMISSED_KEY = 'gg.install-tip.dismissed';

/** Chrome fires this once, early — often before any screen has mounted. */
type InstallPromptEvent = Event & { prompt: () => Promise<void> };

let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // A private window can refuse; then the tip simply never appears.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to remember it in, which only means the tip may come back.
  }
}

/** Already on a home screen (or in a standalone window)? Then there's nothing to offer. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as a Mac, so touch points are what actually distinguish it.
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * A phone or tablet: the only places a home-screen icon means anything. Desktop Chrome
 * offers an install prompt too, but an app window on a laptop isn't what the tip is for.
 * iPads report as Macs, so iOS is checked by name as well as by pointer.
 */
export function isHandheld(): boolean {
  if (typeof window === 'undefined') return false;
  return isIosSafari() || window.matchMedia?.('(pointer: coarse)').matches === true;
}

/**
 * Called once per page load, from `main.tsx`. A visit counter in a lazy `useState` would
 * be double-counted by StrictMode, and one in an effect would need a guard ref; a plain
 * call at startup is neither.
 */
export function noteVisit(): void {
  if (isInstalled()) return;
  const next = visitCount() + 1;
  write(VISITS_KEY, String(Math.min(next, 99)));

  window.addEventListener('beforeinstallprompt', (event) => {
    // Keeping it means we can offer a real install button later, on our own terms.
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    for (const listener of listeners) listener();
  });
}

export function visitCount(): number {
  const raw = Number(read(VISITS_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

export function installTipDismissed(): boolean {
  return read(DISMISSED_KEY) === '1';
}

export function dismissInstallTip(): void {
  write(DISMISSED_KEY, '1');
}

/** The browser's own install flow, when it offered us one. */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

export async function promptInstall(): Promise<void> {
  const event = deferredPrompt;
  if (!event) return;
  // It's single-use: whatever the person chooses, the event is spent.
  deferredPrompt = null;
  await event.prompt();
  for (const listener of listeners) listener();
}

/** Lets a mounted component notice a prompt that arrived after it rendered. */
export function onInstallPromptChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
